// Data-driven storefront top bar — reference layout: nav items on the LEFT, the
// logo CENTERED, cart + user pill on the RIGHT. A 'link' item points at a page
// slug (internal) or external URL; a 'products' item is a dropdown auto-listing
// every Shopify product. Colors/sizes come from the editable nav.style. Used by
// both the public SitePage and the in-place editor.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { SiteNav, NavItem, SiteNavStyle } from '../../../shared/pageBlocks';
import { DEFAULT_NAV_STYLE } from '../../../shared/pageBlocks';
import { shouldShow, inCollection, sortProducts, type StoreProduct } from '@/features/store';
import { useBelow } from './useViewport';
import { useProgressiveTierSrc } from './useProgressiveTierSrc';
import sleepyLogo from '@/assets/sleepy-logo.png';

// server/mediaCdn.js's attachSiteMediaVariants() stashes these as sibling
// fields on PUBLIC nav responses when a ready lightweight variant exists for
// `nav.logoUrl` — same convention as HeroBlockWithVariant in PageRenderer.tsx.
// The editor's in-memory nav never has these (variants are attached
// server-side only), so they're simply undefined there — harmless.
type SiteNavWithVariants = SiteNav & { logoUrlVariantPlaceholderSrc?: string; logoUrlVariantSrc?: string; logoUrlVariantLgSrc?: string };

interface SiteTopBarProps {
  nav: SiteNav;
  products: StoreProduct[];
  onNavigate: (target: string) => void;
  onBrand: () => void;
  /** Right-side controls (cart + user pill). A function so the bar can request a
   *  `compact` user pill when the controls would otherwise overlap the centered
   *  logo. A plain node is still accepted. */
  right?: ((opts: { compact: boolean }) => React.ReactNode) | React.ReactNode;
  editing?: boolean;
  onEditNav?: () => void;
  /** Force the mobile layout (editor mobile preview). Defaults to the live
   *  window width when undefined. */
  isMobile?: boolean;
  /** Product handles that have a builder page — sorted to the top of the
   *  auto-listing products dropdown. */
  productPageHandles?: ReadonlySet<string>;
}

const isExternal = (t: string) => /^https?:\/\//i.test(t);

// Shared dropdown styles (products dropdown + custom dropdowns).
const ddPanel: CSSProperties = {
  position: 'absolute', top: '100%', left: 0, minWidth: 220, maxHeight: 420, overflowY: 'auto',
  background: '#141414', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 6, zIndex: 100,
  boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
};
const ddItem: CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', color: '#e4e4e7', cursor: 'pointer', borderRadius: 6, fontSize: 14 };
const ddHover = (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)');
const ddOut = (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'none');

// A custom collapsible dropdown nav item (user-defined child links).
const DropdownNav: React.FC<{ item: Extract<NavItem, { type: 'dropdown' }>; style: CSSProperties; onNavigate: (t: string) => void }> = ({ item, style, onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button style={style} onClick={() => setOpen((v) => !v)}>{item.label} ▾</button>
      {open && (
        <div style={ddPanel}>
          {item.children.length === 0 ? (
            <div style={{ padding: '8px 10px', color: '#a1a1aa', fontSize: 13 }}>No links yet.</div>
          ) : item.children.map((c) => (
            <button key={c.id} onClick={() => { setOpen(false); onNavigate(c.target); }} style={ddItem} onMouseEnter={ddHover} onMouseLeave={ddOut}>{c.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductsDropdown: React.FC<{ item: Extract<NavItem, { type: 'products' }>; products: StoreProduct[]; style: CSSProperties; onNavigate: (h: string) => void; productPageHandles?: ReadonlySet<string> }> = ({ item, products, style, onNavigate, productPageHandles }) => {
  const [open, setOpen] = useState(false);
  // The collection filter is configured in the inspector (item.collection), not
  // browsed in the dropdown. Empty/undefined = list every sellable product. The
  // list is sorted built-products-first, then price high → low.
  const shown = useMemo(() => {
    const visible = products.filter(shouldShow);
    const filtered = item.collection ? visible.filter((p) => inCollection(p, item.collection!)) : visible;
    return sortProducts(filtered, productPageHandles);
  }, [products, item.collection, productPageHandles]);
  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button style={style} onClick={() => setOpen((v) => !v)}>{item.label} ▾</button>
      {open && (
        <div style={{ ...ddPanel, minWidth: 240 }}>
          {shown.length === 0 ? (
            <div style={{ padding: '8px 10px', color: '#a1a1aa', fontSize: 13 }}>No products yet.</div>
          ) : shown.map((p) => (
            // Navigate by the stable product id (resolved to our internal slug),
            // never the unreliable handle.
            <button key={p.id} onClick={() => { setOpen(false); onNavigate(p.id); }}
              style={ddItem} onMouseEnter={ddHover} onMouseLeave={ddOut}>
              {p.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const SiteTopBar: React.FC<SiteTopBarProps> = ({ nav, products, onNavigate, onBrand, right, editing, onEditNav, isMobile, productPageHandles }) => {
  const narrow = useBelow(640);
  const mobile = isMobile ?? narrow;
  const [menuOpen, setMenuOpen] = useState(false);
  const st: SiteNavStyle = { ...DEFAULT_NAV_STYLE, ...(nav.style || {}) };
  const navVariants = nav as SiteNavWithVariants;
  const logoSrc = useProgressiveTierSrc({
    placeholder: navVariants.logoUrlVariantPlaceholderSrc,
    small: navVariants.logoUrlVariantSrc, large: navVariants.logoUrlVariantLgSrc,
    original: nav.logoUrl || sleepyLogo,
    // The logo renders at logoSize px TALL; ×6 allows even a wide wordmark's
    // width at full sharpness while still letting the auto-quality display
    // cap (abrQuality.ts) stop a nav logo from fetching large/original.
    displayWidthPx: (st.logoSize || 40) * 6,
  });

  // Fit detection: the inline nav, the CENTERED logo, and the right controls must
  // coexist without overlapping. On a desktop viewport too narrow to hold all
  // three we collapse the nav to the hamburger; when the right controls would
  // reach the logo we compact the user pill (avatar + caret only). Natural widths
  // are captured while expanded and frozen while collapsed/compact so the toggle
  // can't oscillate.
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const reqNavW = useRef(0);
  const reqRightW = useRef(0);
  const [collapsed, setCollapsed] = useState(false);
  const [compact, setCompact] = useState(false);

  // The measure fn is kept in a ref so the ResizeObserver (set up once) always
  // calls the latest closure without re-subscribing every render.
  const measureRef = useRef<() => void>(() => {});
  measureRef.current = () => {
    const h = headerRef.current;
    if (!h) return;
    const W = h.clientWidth;
    const logoZone = st.logoSize + 24; // logo + breathing room on each side
    // The right controls overlap the centered logo once they pass its left edge.
    setCompact(reqRightW.current > W / 2 - st.logoSize / 2 - 12);
    // Desktop only: nav + logo + right must all fit on one line, else hamburger.
    // (A real/previewed phone already uses the hamburger.)
    setCollapsed(!mobile && reqNavW.current + logoZone + reqRightW.current + 40 > W);
  };

  // Capture natural (content) widths while expanded/non-compact, then re-evaluate
  // fit — every render, so a login (right controls change) or a nav edit is caught.
  // Widths are frozen while collapsed/compact so the toggle can't oscillate.
  useLayoutEffect(() => {
    if (navRef.current && !collapsed) reqNavW.current = navRef.current.scrollWidth;
    if (rightRef.current && !compact) reqRightW.current = rightRef.current.scrollWidth;
    measureRef.current();
  });

  // Re-measure on viewport/header resize. Observer created once (mount-only).
  useEffect(() => {
    const onResize = () => measureRef.current();
    const ro = new ResizeObserver(onResize);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener('resize', onResize);
    return () => { ro.disconnect(); window.removeEventListener('resize', onResize); };
  }, []);

  // Hamburger replaces the inline nav on a phone OR a too-narrow desktop (never in
  // edit mode, where the items must stay visible/editable).
  const hamburger = !editing && (mobile || collapsed);
  // Widening back to the inline nav (or entering edit mode) must drop any open
  // dropdown, else it reappears the next time the hamburger returns.
  useEffect(() => { if (!hamburger) setMenuOpen(false); }, [hamburger]);
  const rightNode = typeof right === 'function' ? (right as (o: { compact: boolean }) => React.ReactNode)({ compact }) : right;
  const navBtn: CSSProperties = {
    // No fontFamily/fontWeight/fontStyle here — inherit the site theme defaults
    // from the shell (siteShellStyle) so the nav matches the rest of the site.
    background: 'none', border: 'none', color: st.textColor, cursor: 'pointer',
    fontSize: st.fontSize, padding: mobile ? '8px 6px' : '6px 4px', whiteSpace: 'nowrap',
    textDecoration: 'none',
  };
  // Per-item style: optional color override + individual underline, falling back
  // to the bar-wide style. Applied to every item variant (link/products/dropdown).
  const itemStyle = (item: NavItem): CSSProperties => ({
    ...navBtn,
    ...(item.color ? { color: item.color } : null),
    textDecoration: item.underline ? 'underline' : 'none',
  });
  const logo = (
    // ALWAYS absolutely centered (desktop AND mobile) so the logo sits dead-center
    // of the bar regardless of how wide the left (nav/hamburger) or right (cart +
    // user pill) sides are — a logged-in user's pill used to shove it off-center.
    <button onClick={editing ? undefined : onBrand} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
      <img src={logoSrc} alt={nav.brandName || 'Home'} style={{ height: st.logoSize, width: st.logoSize, opacity: 0.95, objectFit: 'contain' }} />
    </button>
  );
  const go = (target: string) => { setMenuOpen(false); onNavigate(target); };
  return (
    <>
      {/* In edit mode the whole bar is one selection target (like a block) — click
          anywhere on it to select the nav and open its inspector. */}
      <header
        ref={headerRef}
        onClick={editing ? onEditNav : undefined}
        title={editing ? 'Click to edit navigation' : undefined}
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          display: 'flex', alignItems: 'center', gap: mobile ? 8 : 0,
          height: st.height, padding: mobile ? '0 10px' : '0 20px',
          background: st.bgColor,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          outline: editing ? '2px dashed rgba(59,130,246,0.5)' : 'none', outlineOffset: -4,
          cursor: editing ? 'pointer' : 'default',
        }}
      >
        {hamburger ? (
          <button onClick={() => setMenuOpen((v) => !v)} aria-label="Menu"
            className="flex flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/60 p-2 text-gray-200 transition-colors hover:bg-black/90 touch-manipulation"
            style={{ flexShrink: 0, height: 40 }}>
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
          </button>
        ) : (
          /* nav items. The inner flex wrapper (navRef) is content-sized so we can
             measure the nav's NATURAL width for fit detection (the outer nav is
             flex:1). NEVER a scroll container: an overflow:auto ancestor clips
             the absolutely positioned dropdown panels — on Windows the classic
             scrollbars it spawned also shifted the row mid-click, closing the
             dropdown and eating every click. Live overflow can't happen (fit
             detection collapses to the hamburger); edit mode (display-only
             items) clips scrollbar-free instead. */
          <nav style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflowX: editing ? 'hidden' : 'visible' }}>
            <div ref={navRef} style={{ display: 'flex', alignItems: 'center', gap: mobile ? 10 : 18, flexWrap: 'nowrap' }}>
              {nav.items.map((item) => {
                const s = itemStyle(item);
                if (editing) return <span key={item.id} style={s}>{item.label}{(item.type === 'products' || item.type === 'dropdown') ? ' ▾' : ''}</span>;
                if (item.type === 'products') return <ProductsDropdown key={item.id} item={item} products={products} style={s} onNavigate={onNavigate} productPageHandles={productPageHandles} />;
                if (item.type === 'dropdown') return <DropdownNav key={item.id} item={item} style={s} onNavigate={onNavigate} />;
                return isExternal(item.target)
                  ? <a key={item.id} href={item.target} style={s} target="_blank" rel="noreferrer">{item.label}</a>
                  : <button key={item.id} style={s} onClick={() => onNavigate(item.target)}>{item.label}</button>;
              })}
            </div>
          </nav>
        )}

        {/* Center: logo only (no text), absolutely centered. */}
        {logo}

        {/* Right: cart + user pill — stay interactive even in edit mode. Grows +
            right-aligns so the absolutely-centered logo stays centered; the inner
            wrapper (rightRef) is content-sized for natural-width fit detection. */}
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
          <div ref={rightRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{rightNode}</div>
        </div>
      </header>

      {/* Mobile dropdown menu (matches the app's translucent panels). */}
      {hamburger && menuOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60" onClick={() => setMenuOpen(false)} />
          <div className="fixed inset-x-0 z-50 max-h-[70dvh] overflow-y-auto border-b border-white/10 bg-black/90" style={{ top: st.height }}>
            {nav.items.map((item) => {
              if (item.type === 'products') {
                const visible = products.filter(shouldShow);
                const filtered = item.collection ? visible.filter((p) => inCollection(p, item.collection!)) : visible;
                const shown = sortProducts(filtered, productPageHandles);
                return (
                  <div key={item.id}>
                    <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-zinc-500">{item.label}</div>
                    {shown.length === 0 ? (
                      <div className="px-6 pb-2 text-sm text-zinc-500">No products yet.</div>
                    ) : shown.map((p) => (
                      <button key={p.id} onClick={() => go(p.handle)} className="block w-full px-6 py-3 text-left text-[15px] text-zinc-100 hover:bg-white/5 touch-manipulation">{p.title}</button>
                    ))}
                  </div>
                );
              }
              if (item.type === 'dropdown') return (
                <div key={item.id}>
                  <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-zinc-500">{item.label}</div>
                  {item.children.map((c) => (
                    <button key={c.id} onClick={() => (isExternal(c.target) ? window.open(c.target, '_blank') : go(c.target))} className="block w-full px-6 py-3 text-left text-[15px] text-zinc-100 hover:bg-white/5 touch-manipulation">{c.label}</button>
                  ))}
                </div>
              );
              return (
                <button key={item.id} onClick={() => (isExternal(item.target) ? window.open(item.target, '_blank') : go(item.target))}
                  className="block w-full border-b border-white/5 px-4 py-3 text-left text-[15px] text-zinc-100 hover:bg-white/5 touch-manipulation">{item.label}</button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default SiteTopBar;
